import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronUp } from 'lucide-react';

/**
 * Where the hold in hand sits in a circuit, and the two ways it can be moved
 * from there. Absent on an ordinary climb, whose holds are a set: there is no
 * order to show and nothing to step along.
 */
export interface HoldStepControl {
  /**
   * Its place in the run, counted from 1 — what the button wears. Null when
   * nothing in hand is a step of the run: no hold picked up, or a foot, which
   * the run passes over. The button then stands dead, so it keeps its place in
   * the bar rather than the bar changing shape under the thumb.
   */
  order: string | null;
  /**
   * The two halves of the button, named by what they do to that number: the top
   * one raises it (the hold is climbed later), the bottom one lowers it. That is
   * the way round the arrows point, and the number between them is what the
   * setter is reading while they press.
   */
  onIncrease: () => void;
  onDecrease: () => void;
  canIncrease: boolean;
  canDecrease: boolean;
}

/**
 * The hold's place in the circuit as one button split in two: press the top half
 * and its number goes up, the bottom half and it goes down, with the number it
 * currently wears between them.
 *
 * One control rather than three, because the number is what the two halves are
 * *about* — it is the answer the setter is reading while they press, and it is
 * the same number the circle wears on the photo. The halves go dead at the ends
 * of the run and where the move is not the setter's to make: a hold climbed
 * through cannot be dragged in front of a start or past the top.
 */
export function HoldStepButton({ step }: { step: HoldStepControl }) {
  const { t } = useTranslation();

  return (
    <div
      className="relative size-9 shrink-0 overflow-hidden rounded-full border border-border"
      data-testid="hold-step"
    >
      <button
        type="button"
        onClick={step.onIncrease}
        disabled={!step.canIncrease}
        aria-label={t('climbEditor.circuit.moveLater')}
        title={t('climbEditor.circuit.moveLater')}
        className="flex h-1/2 w-full items-start justify-center pt-0.5 text-muted-foreground transition-colors hover:text-foreground disabled:cursor-not-allowed disabled:opacity-30"
      >
        <ChevronUp className="size-3" aria-hidden />
      </button>

      <button
        type="button"
        onClick={step.onDecrease}
        disabled={!step.canDecrease}
        aria-label={t('climbEditor.circuit.moveEarlier')}
        title={t('climbEditor.circuit.moveEarlier')}
        className="flex h-1/2 w-full items-end justify-center pb-0.5 text-muted-foreground transition-colors hover:text-foreground disabled:cursor-not-allowed disabled:opacity-30"
      >
        <ChevronDown className="size-3" aria-hidden />
      </button>

      {/* Across the seam between the two halves, and deaf to presses: whichever
          half the thumb lands on is the half that was meant. A dash where there
          is no number, so the button reads as waiting rather than as broken. */}
      <span
        className={`pointer-events-none absolute inset-0 flex items-center justify-center text-[10px] font-bold tabular-nums ${
          step.order === null ? 'text-muted-foreground/60' : ''
        }`}
        data-testid="hold-step-order"
      >
        {step.order ?? '–'}
      </span>
    </div>
  );
}
